import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";

import { AddPurchaseOrderItemDialogComponent } from "@components/purchase-orders/components/add-purchase-order-item-dialog/add-purchase-order-item-dialog.component";
import { PurchaseOrderPayload } from "@components/purchase-orders/models/purchase-order-payload.type";
import { PurchaseOrderStatus } from "@components/purchase-orders/models/purchase-order-status.enum";
import {
  ILineITem,
  ILineITemExtended,
  IPurchaseOrderExtended,
} from "@components/purchase-orders/models/purchase-order.interface";
import { PurchaseOrdersService } from "@components/purchase-orders/purchase-orders.service";
import { OverlayDocumentAttachmentsComponent } from "@components/shared";
import { SlideOverlayContentComponent } from "@components/shared/overlay/slide-overlay-content/slide-overlay-content.component";
import { SlideOverlayPageClass } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.class";
import { TextConstants } from "@shared/constants";
import {
  AttachmentTargetEnum,
  EntityTypeEnum,
  OverlayTabEnum,
  RecordStateEnum,
  RouteEnum,
} from "@shared/enums";
import { IRecordState, ISelectOption } from "@shared/interfaces";
import {
  CommonService,
  ConnectionsService,
  DeliveriesService,
  LocationsService,
} from "@shared/services";
import { FormUtils, LocalizationUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

import { PurchaseOrdersOverlayService } from "./../../purchase-orders-overlay.service";

@Component({
  selector: "app-purchase-order-overlay",
  templateUrl: "./purchase-order-overlay.component.html",
  styleUrl: "./purchase-order-overlay.component.scss",

  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class PurchaseOrderOverlayComponent
  extends SlideOverlayPageClass
  implements OnInit, OnDestroy
{
  private readonly purchaseService = inject(PurchaseOrdersService);

  private readonly connectionsService = inject(ConnectionsService);

  private readonly locationsService = inject(LocationsService);

  private readonly commonService = inject(CommonService);

  private readonly deliveriesService = inject(DeliveriesService);

  public readonly purchaseOrdersOverlayService = inject(PurchaseOrdersOverlayService);

  public readonly purchaseOrderStatusEnum = PurchaseOrderStatus;

  override element: IPurchaseOrderExtended = null;

  override entityType: EntityTypeEnum = EntityTypeEnum.PURCHASE_ORDERS;

  public override attachmentTargetType = AttachmentTargetEnum.PURCHASE_ORDERS;

  async ngOnInit(): Promise<void> {
    this.overlay.showLoading();

    if (!this.isOnCorrectOverlay(RouteEnum.OVERLAY_PURCHASE_ORDER)) {
      return;
    }
    Promise.all([await this.onReloadOrganisations(), await this.onReloadLocations()]);

    if (this.recordId) {
      this.setCountersToLoadingState();

      const elementLoaded = await this.reloadElement(this.recordId);

      if (!elementLoaded) {
        return;
      }

      this.loadCounters();
      await this.setMenuItemFromURLParam();
    } else {
      this.setupForm();
    }
    this.overlay.dismissLoading();
  }

  public organisationsOptions = signal<ISelectOption[]>([]);

  public activeOrganisationOption = signal<ISelectOption>(null);

  public locationsOptions = signal<ISelectOption[]>([]);

  public readonly countryOptions = computed(() => this.commonService.countriesOptions());

  public deliveriesIds = signal<string[]>([]);

  public lineItems = signal<ILineITemExtended[]>([]);

  public readonly attachmentTargetEnum = AttachmentTargetEnum;

  private activeOrganisation = this.authenticationService.getActiveOrganisation();

  public readonly translations = {
    orderItemsTp: $localize`:@@purchaseorders.titletooltip:Purchase order items state which products and in which quantities need to be provided as part of fulfilling the purchase order.`,
  };

  @ViewChild("slideOverlayContent") override slideOverlayContent: SlideOverlayContentComponent;

  @ViewChild("documentSection") documentSection: OverlayDocumentAttachmentsComponent;

  override menuItems = signal(
    new Map([
      [OverlayTabEnum.DETAILS, { title: TextConstants.PURCHASE_ORDER_DETAILS, isEnabled: true }],
      [
        OverlayTabEnum.ITEMS,
        {
          title: $localize`:@@purchaseorders.menutitle:Purchase order items`,
          isEnabled: false,
          tooltip: this.translations.orderItemsTp,
        },
      ],
      [OverlayTabEnum.DELIVERIES, { title: TextConstants.DELIVERIES, isEnabled: false }],
      [OverlayTabEnum.DOCUMENTS, { title: TextConstants.DOCUMENTS, isEnabled: false }],
      [
        OverlayTabEnum.COMMENTS,
        { title: TextConstants.COMMENTS, isEnabled: false, isHidden: !this.isRegularUser },
      ],
    ]),
  );

  formGroup: FormGroup;

  private onReloadOrganisations = async (): Promise<void> => {
    try {
      const allOrganisations = await this.connectionsService.getAll();
      const currentOrganisationOption = {
        value: this.activeOrganisation.id,
        label: this.activeOrganisation.name,
      };
      const organisationsOptions = allOrganisations
        .filter((org) => org.recordState === RecordStateEnum.ACTIVE)
        .map((org) => ({ label: org.name, value: org.id }));

      this.activeOrganisationOption.set(currentOrganisationOption);
      this.organisationsOptions.set([currentOrganisationOption, ...organisationsOptions]);
    } catch (error) {
      this.notificationService.showError(error);
    }
  };

  private onReloadLocations = async (): Promise<void> => {
    try {
      const locations = await this.locationsService.getAll();

      const locationsOptions = locations
        .filter((loc) => loc.recordState === RecordStateEnum.ACTIVE)
        .map((loc) => ({ label: loc.name, value: loc.id }));

      this.locationsOptions.set(locationsOptions);
    } catch (error) {
      this.notificationService.showError(error);
    }
  };

  public override async save(): Promise<boolean> {
    if (await FormUtils.isInvalid(this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return false;
    }
    this.overlay.closeSideMenu();
    this.overlay.showLoading();
    const payload = this.getSavePayload();

    try {
      const updatedElement = await this.purchaseService.createOrUpdate(payload, this.element?.id);
      const element = await this.purchaseService.getByIdsGraphQL([updatedElement.id]);

      this.element = element[0];

      this.notificationService.showSuccess(
        this.isEditing()
          ? $localize`:@@purchase-order-overlay.ordermodified:Purchase order modified`
          : $localize`:@@purchase-order-overlay.ordercreated:Purchase order created`,
      );

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    } finally {
      this.overlay.dismissLoading();
    }
  }

  getSavePayload() {
    const activeOrgId = this.authenticationService.getActiveOrganisationId();
    const formValue = this.formGroup.value;
    const buyerUri =
      activeOrgId === formValue.buyer.value
        ? `/organisations/${activeOrgId}`
        : `/organisations/${activeOrgId}/connections/${formValue.buyer.value}`;
    const supplierUri =
      activeOrgId === formValue.buyer.value
        ? `/organisations/${activeOrgId}`
        : `/organisations/${activeOrgId}/connections/${formValue.supplier.value}`;
    const status = this.element ? formValue.status?.value : PurchaseOrderStatus.OPEN;
    let lineItems = [];

    if (this.isEditing()) {
      lineItems = this.element.lineItems.map((item) => ({
        lineItemId: item.lineItemId,
        product: `/organisations/${activeOrgId}/products/${item.product.id}`,
        quantity: item.quantity,
      }));
    }
    const payload: PurchaseOrderPayload = {
      number: formValue.purchaseOrderId,
      orderDate: FormUtils.getDateValueForPayload(formValue.orderDate),
      expectedDeliveryDate: FormUtils.getDateValueForPayload(formValue.expectedDeliveryDate),
      note: formValue.note,
      buyer: buyerUri,
      supplier: supplierUri,
      lineItems,
      status,
    };

    if (formValue.deliverToLocation) {
      payload.deliverToLocation = `/organisations/${activeOrgId}/locations/${formValue.deliverToLocation.value}`;
    }

    return payload;
  }

  public override reloadElement = async (id: string): Promise<boolean> => {
    this.overlay.showLoading();

    try {
      const element = await this.purchaseService.getByIdsGraphQL([id], null, ["DOCUMENTS"]);

      this.element = element[0];

      this.purchaseService.setCurrentPurchaseOrder(this.element);
      this.initialTags = [];

      const deliveries = await this.deliveriesService.getAll(null, this.element.number);

      this.deliveriesIds.set(deliveries.map((d) => d.id));
      this.purchaseOrdersOverlayService.deliveriesCount.set(this.deliveriesIds().length);
      this.purchaseOrdersOverlayService.lineItemsCount.set(this.element.lineItems?.length);
      this.setEditMode();

      this.setupForm();

      this.overlay.dismissLoading();

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    }
  };

  public override get isSubmitButtonDisabled(): boolean {
    if (!this.isEditing()) {
      return false;
    }

    return (
      !this.formGroup ||
      this.formGroup.invalid ||
      this.formGroup.pending ||
      !this.hasFormValuesChanged()
    );
  }

  override setupForm(): void {
    let supplierValue: ISelectOption;
    let buyerValue: ISelectOption;
    let deliverToLocationValue: ISelectOption;
    let statusValue: ISelectOption;
    const entityExistsValidatorArgs = {
      searchPropertyName: "number",
      searchPropertyErrorDisplayName: TextConstants.NUMBER.toLocaleLowerCase(),
    };

    if (this.isEditing()) {
      const supplier = this.element?.supplier ?? this.activeOrganisation;
      const buyer = this.element?.buyer ?? this.activeOrganisation;

      supplierValue = this.organisationsOptions().find((org) => org.value === supplier.id);
      buyerValue = this.organisationsOptions().find((org) => org.value === buyer.id);
      deliverToLocationValue = this.locationsOptions().find(
        (loc) => loc.value === this.element?.deliverToLocation?.id,
      );
      statusValue = {
        label: LocalizationUtils.getStatus(this.element.status),
        value: this.element.status,
      };
    }

    this.formGroup = new FormGroup(
      {
        purchaseOrderId: new FormControl(
          this.element?.number ?? null,
          [CustomValidators.required],
          [
            CustomValidators.entityAlreadyExists(
              this.purchaseService,
              this.element?.id ?? this.route.snapshot.params["id"] ?? null,
              entityExistsValidatorArgs,
            ),
          ],
        ),
        expectedDeliveryDate: new FormControl(this.element?.expectedDeliveryDate ?? null),
        orderDate: new FormControl(this.element?.orderDate ?? null),
        supplier: new FormControl(supplierValue, [CustomValidators.required]),
        buyer: new FormControl(buyerValue, [CustomValidators.required]),
        deliverToLocation: new FormControl(deliverToLocationValue),
        note: new FormControl(this.element?.note ?? null),
        status: new FormControl(statusValue),
      },
      CustomValidators.dateRules([
        {
          name: "expectedDeliveryDate",
          rules: [
            {
              type: "<",
              target: "orderDate",
              errorMessage: $localize`:@@purchase-order-overlay.error.earlierthanorder:Cannot be earlier than order date`,
            },
          ],
        },
      ]),
    );

    if (this.isEditing()) {
      FormUtils.setControlsRequiredValidator(this.formGroup, ["status"], true, false);
    }
    this.initialFormValue = this.formGroup.value;
    this.hasFormValuesChanged.set(false);

    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.hasFormValuesChanged.set(this.hasInitialFormValueChanged(this.formGroup.value));
      }),
    );
  }

  async updateBuyerOrganisation(orgId: string) {
    await this.onReloadOrganisations();
    const organisation = this.organisationsOptions().find((org) => org.value === orgId);

    if (organisation) {
      setTimeout(() => {
        this.formGroup.controls["buyer"].setValue(organisation);
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  async updateSupplierOrganisation(orgId: string) {
    await this.onReloadOrganisations();
    const organisation = this.organisationsOptions().find((org) => org.value === orgId);

    if (organisation) {
      setTimeout(() => {
        this.formGroup.controls["supplier"].setValue(organisation);
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  async updateDeliverToLocation(locationId: string) {
    await this.onReloadLocations();
    const location = this.locationsOptions().find((location) => location.value === locationId);

    if (location) {
      setTimeout(() => {
        this.formGroup.controls["deliverToLocation"].setValue(location);
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  async onAddPurchaseOrderItem() {
    const dialogRef = this.dialog.open(AddPurchaseOrderItemDialogComponent, {
      data: {
        allLineItems: this.element?.lineItems ?? [],
      },
    });

    dialogRef.afterClosed().subscribe(async (result: { hasSaved: boolean; item: ILineITem }) => {
      if (result?.hasSaved) {
        await this.reloadElement(this.element.id);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  async onRemoveLineItem(lineItemId: string) {
    try {
      await this.purchaseService.removeLineItem(lineItemId);
      await this.reloadElement(this.element.id);
      this.notificationService.showSuccess(
        $localize`:@@purchase-order-overlay.lineitemremoved:Line item removed`,
      );
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  setCountersToLoadingState() {
    this.purchaseOrdersOverlayService.setCountersToLoadingState();
  }

  async onLineItemUpdated() {
    await this.reloadElement(this.element.id);
  }

  loadCounters(): void {
    if (this.element.id) {
      this.loadDocuments();
      this.loadComments();
    }
  }

  loadDocuments() {
    this.purchaseOrdersOverlayService.loadDocumentCounter(
      this.element.id,
      this.attachmentTargetType,
    );
  }

  loadComments() {
    this.purchaseOrdersOverlayService.loadCommentCounter(this.entityUri);
  }

  setCountersEmptyState() {
    this.purchaseOrdersOverlayService.setCountersEmptyState();
  }

  override recordName(): string {
    return this.element?.number;
  }

  protected override deleteRecord(id: string): Promise<void> {
    return this.purchaseService.delete(id);
  }

  public override async afterSave(isSaveOnly: boolean): Promise<void> {
    if (isSaveOnly && !this.isEditing()) {
      await this.routerService.navigate(this.routerService.getPurchaseOrderLink(this.element.id), {
        replaceUrl: true,
      });
    } else {
      this.reloadElement(this.element.id);
    }
  }

  protected override async archiveRecord(id: string, payload: IRecordState): Promise<void> {
    await this.purchaseService.setRecordState(payload, id);
  }

  ngOnDestroy(): void {
    this.setCountersEmptyState();
  }
}
